import { useEffect, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api, errText, fetcher } from "@/lib/api";

export function JournalPanel({ position, onSaved }) {
  const { data: meta } = useSWR("/journal/tags", fetcher);
  const [note, setNote] = useState("");
  const [tags, setTags] = useState([]);
  const [rating, setRating] = useState(null);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const j = position?.journal;
    setNote(j?.note || ""); setTags(j?.tags || []); setRating(j?.rating ?? null);
  }, [position?.id, position?.journal]);
  if (!position) return null;
  const toggle = (t) => setTags(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);
  const addCustom = () => { const t = custom.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); if (t && !tags.includes(t)) setTags([...tags, t]); setCustom(""); };
  const save = async () => {
    setBusy(true);
    try { await api.put(`/positions/${position.id}/journal`, { note, tags, rating }); toast.success("Journal saved"); onSaved?.(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const suggested = [...new Set([...(meta?.suggested || []), ...tags])];
  const isOpen = position.status === "open";
  return (
    <div className="bg-[#1e2329] rounded p-2" data-testid="journal-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold tracking-widest text-[#fcd535]">JOURNAL · {isOpen ? "pre-mortem plan" : "post-trade review"}</div>
        <div className="flex items-center gap-0.5" data-testid="journal-rating">
          {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(rating === n ? null : n)} className="p-0.5" data-testid={`journal-rating-${n}`} aria-label={`rate ${n}`}><Star size={12} className={n <= (rating || 0) ? "fill-[#fcd535] text-[#fcd535]" : "text-[#5e6673]"} /></button>)}
        </div>
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={isOpen ? "Plan: exit if funding flips positive; invalidation below 4h swing low…" : "What happened, what you'd do differently, what worked…"} className="input mt-1.5 text-[11px] resize-y min-h-[56px]" data-testid="journal-note-input" />
      <div className="flex flex-wrap gap-1 mt-1.5" data-testid="journal-tags">
        {suggested.map((t) => <button key={t} onClick={() => toggle(t)} className={`pill transition-colors ${tags.includes(t) ? "bg-[rgba(252,213,53,0.18)] text-[#fcd535] border border-[#fcd535]/40" : "bg-[#2b3139] text-[#848e9c] hover:text-white"}`} data-testid={`journal-tag-${t}`}>{t}</button>)}
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom()} placeholder="custom tag" className="input h-7 py-0 text-[11px] flex-1" data-testid="journal-custom-tag-input" />
        <button onClick={addCustom} className="btn-ghost rounded px-2 h-7 text-[10px]" data-testid="journal-add-tag-button">Add</button>
        <button onClick={save} disabled={busy} className="btn-yellow rounded px-3 h-7 text-[10px]" data-testid="journal-save-button">{busy ? "Saving…" : "SAVE NOTE"}</button>
      </div>
      {position.journal?.updated_at && <div className="text-[10px] text-[#5e6673] mono mt-1" data-testid="journal-updated">last saved {new Date(position.journal.updated_at).toLocaleString("en-GB", { hour12: false })}</div>}
    </div>
  );
}
